import React, { useState, useEffect } from "react";
import styles from "./styles.module.scss";
import {
  Field,
  FieldGroup,
  FormCard,
  SubmitButtonMail,
  InputFieldText,
  InputFieldSelect,
} from "../../reusable";
import { useClaimData } from "../../../claimProvider";
import { FormPartInterface, SaveValueInterface } from "../../../interfaces";
import { Checkbox } from "../../reusable/checkbox";

export const Review = (props: FormPartInterface) => {
  const { isActive, assignGroup, saveValueFunct } = props;
  const [currentStep, setCurrentStep] = useState(0);
  const [postCurrentStep, setPostCurrentStep] = useState(0);
  const [formValid, setFormValid] = useState(false);
  const [isInfoConfirmed, setIsInfoConfirmed] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const claim = useClaimData() || {};
  const [isContactConsentConfirmed, setIsContactConsentConfirmed] =
    useState(false);
  const [isMarketingConsentConfirmed, setIsMarketingConsentConfirmed] =
    useState(false);
  const [localSideEffects, setLocalSideEffects] = useState<{
    had: "" | "Yes" | "No";
    details: string;
  }>({ had: "", details: "" });
  const [requiredHospitalisation, setRequiredHospitalisation] = useState<
    "" | "Yes" | "No"
  >("");

  useEffect(() => {
    if (isActive !== "review") return;

    if (!claim || Object.keys(claim).length === 0) {
      console.warn("No claim data available in review step");
      setFormValid(false);
      setValidationMessage(
        "No claim data available. Please complete the previous steps."
      );
      return;
    }

    const requiredFields = {
      address: [],
      personal: ["firstname", "lastname", "dob"],
      contact: ["telephone", "email"],
      medication: [
        "medication_drug",
        "start_month",
        "start_year",
        "is_still_taking",
        "obtained_from",
      ],
    };

    let missingFields: string[] = [];
    let isValid = true;

    for (const field of requiredFields.address) {
      if (!claim[field] || claim[field].trim() === "") {
        isValid = false;
        missingFields.push(field);
      }
    }

    for (const field of requiredFields.personal) {
      if (!claim[field] || claim[field].trim() === "") {
        isValid = false;
        missingFields.push(field);
      }
    }

    for (const field of requiredFields.contact) {
      if (!claim[field] || claim[field].trim() === "") {
        isValid = false;
        missingFields.push(field);
      }
    }

    // Check if information is confirmed (first checkbox is required)
    if (!isInfoConfirmed) {
      isValid = false;
      missingFields.push("information_confirmed");
    }

    setFormValid(isValid);

    if (!isValid) {
      if (missingFields.includes("information_confirmed")) {
        setValidationMessage(
          "Please confirm that the information provided is correct to proceed."
        );
      } else {
        setValidationMessage(
          "Some required information is missing. Please go back and complete all required fields."
        );
      }
    } else {
      setValidationMessage("");
    }

    // Check existing consent values
    if (claim.contact_consent === "yes") {
      setIsContactConsentConfirmed(true);
    }
    if (claim.marketing_consent === "yes" || claim.marketing_consent === "1") {
      setIsMarketingConsentConfirmed(true);
    }
    // initialise adverse effect local state from claim if present
    if (claim.had_side_effects) {
      setLocalSideEffects({
        had: claim.had_side_effects,
        details: claim.side_effect_details || "",
      });
    }
    if (claim.required_hospitalisation) {
      setRequiredHospitalisation(claim.required_hospitalisation);
    }
  }, [claim, isActive, isInfoConfirmed]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      return dateString;
    }
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString("en-GB");
      }
    } catch (error) {
      console.error("Error formatting date:", error);
    }
    return dateString;
  };

  const formatPreviousAddresses = () => {
    try {
      const previousAddressesData = claim.previous_addresses;
      if (!previousAddressesData) return null;

      let addresses = [];
      if (typeof previousAddressesData === "string") {
        addresses = JSON.parse(previousAddressesData);
      } else if (Array.isArray(previousAddressesData)) {
        addresses = previousAddressesData;
      }

      if (!addresses || addresses.length === 0) return null;

      return addresses;
    } catch (error) {
      console.error("Error parsing previous addresses:", error);
      return null;
    }
  };

  const handleBack = () => {
    assignGroup("adverse");
  };

  const handleContinue = async () => {
    let errors: string[] = [];

    if (!formValid) {
      errors.push("Please complete all required fields.");
    }

    if (!isInfoConfirmed) {
      errors.push("Please confirm the information is correct.");
    }

    if (errors.length > 0) {
      setValidationMessage(errors.join(" "));
      return;
    }

    // Save adverse effects responses
    if (localSideEffects.had) {
      saveValueFunct({
        field: "had_side_effects",
        value: localSideEffects.had,
      });
      saveValueFunct({
        field: "side_effect_details",
        value: localSideEffects.details || "",
      });
    }
    if (requiredHospitalisation) {
      saveValueFunct({
        field: "required_hospitalisation",
        value: requiredHospitalisation,
      });
    }

    // Ensure marketing consent is saved as "1" or "0" for backend compatibility
    if (isMarketingConsentConfirmed) {
      saveValueFunct({
        field: "marketing_consent",
        value: "1",
      });
    } else {
      saveValueFunct({
        field: "marketing_consent",
        value: "0",
      });
    }

    assignGroup("complete");
  };

  const getCurrentStep = () => {
    return isActive === "review" ? (
      <FieldGroup title="Review Your Information">
        <FormCard
          assignPostStep={setPostCurrentStep}
          step={currentStep}
          onBack={handleBack}
          isFirstStep={false}
        >
          <h3>Please Review Your Information</h3>

          {formatPreviousAddresses() && (
            <div className={styles.reviewSection}>
              <h3>Previous Addresses (Last 3 Years)</h3>
              {formatPreviousAddresses()?.map((address: any, index: number) => (
                <div
                  key={index}
                  className={styles.reviewItem}
                  style={{ marginBottom: "15px" }}
                >
                  <p className={styles.reviewLabel}>Address {index + 1}:</p>
                  <p className={styles.reviewValue}>
                    {address.line_one}
                    {address.line_two && (
                      <>
                        <br />
                        {address.line_two}
                      </>
                    )}
                    {address.line_three && (
                      <>
                        <br />
                        {address.line_three}
                      </>
                    )}
                    <br />
                    {address.town}
                    {address.county && `, ${address.county}`}
                    <br />
                    {address.postcode}
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className={styles.reviewSection}>
            <h3>Personal Information</h3>
            <div className={styles.reviewItem}>
              <p className={styles.reviewLabel}>Name:</p>
              <p className={styles.reviewValue}>
                {claim?.title || ""} {claim?.firstname || ""}{" "}
                {claim?.lastname || ""}
              </p>
            </div>
            <div className={styles.reviewItem}>
              <p className={styles.reviewLabel}>Date of Birth:</p>
              <p className={styles.reviewValue}>
                {formatDate(claim?.dob || "")}
              </p>
            </div>
          </div>

          <div className={styles.reviewSection}>
            <h3>Medication Information</h3>
            <div className={styles.reviewItem}>
              <p className={styles.reviewLabel}>Medication:</p>
              <p className={styles.reviewValue}>{claim?.medication || ""}</p>
            </div>
            <div className={styles.reviewItem}>
              <p className={styles.reviewLabel}>Purpose:</p>
              <p className={styles.reviewValue}>{claim?.usage_reason || ""}</p>
            </div>
            <div className={styles.reviewItem}>
              <p className={styles.reviewLabel}>Start date:</p>
              <p className={styles.reviewValue}>
                {claim?.medication_start_month || ""}/
                {claim?.medication_start_year || ""}
              </p>
            </div>
            {claim?.is_still_taking === "false" && (
              <div className={styles.reviewItem}>
                <p className={styles.reviewLabel}>End date:</p>
                <p className={styles.reviewValue}>
                  {claim?.medication_end_month || ""}/
                  {claim?.medication_end_year || ""}
                </p>
              </div>
            )}
            <div className={styles.reviewItem}>
              <p className={styles.reviewLabel}>Still taking:</p>
              <p className={styles.reviewValue}>
                {claim?.is_still_taking === "1" ? "Yes" : "No"}
              </p>
            </div>
            <div className={styles.reviewItem}>
              <p className={styles.reviewLabel}>Obtained from:</p>
              <p className={styles.reviewValue}>
                {claim?.medication_source || ""}
              </p>
            </div>
          </div>

          <div className={styles.reviewSection}>
            <h3>Adverse Effects</h3>
            <div className={styles.reviewItem}>
              <p className={styles.reviewLabel}>Experienced side effects:</p>
              <p className={styles.reviewValue}>
                {claim?.had_side_effects === "1" ? "Yes" : "No"}
              </p>
            </div>
            {claim?.had_side_effects === "1" && (
              <div className={styles.reviewItem}>
                <p className={styles.reviewLabel}>Details:</p>
                <p className={styles.reviewValue}>
                  {claim?.side_effect_details || ""}
                </p>
              </div>
            )}
          </div>

          <Field fieldLabel="">
            <Checkbox
              fieldName="information_confirmed"
              label="I confirm that the information provided is correct to the best of my knowledge and I am happy to be contacted by Sentinel Legal Limited in relation to my enquiry."
              initialValue={isInfoConfirmed ? "yes" : "no"}
              saveValue={(data) => {
                const ok = data.value === "yes";
                setIsInfoConfirmed(ok);
                return saveValueFunct({
                  field: "information_confirmed",
                  value: ok ? "yes" : "no",
                });
              }}
            />
          </Field>

          <Field fieldLabel="">
            <Checkbox
              fieldName="marketing_consent"
              label="I consent to receiving updates, offers, and information from Sentinel Legal about related legal services and promotions via email, phone, or SMS. I understand I can opt out at any time by contacting Sentinel Legal or using the unsubscribe link in any message."
              initialValue={isMarketingConsentConfirmed ? "yes" : "no"}
              saveValue={(data) => {
                const ok = data.value === "yes";
                setIsMarketingConsentConfirmed(ok);
                return saveValueFunct({
                  field: "marketing_consent",
                  value: ok ? "1" : "0",
                });
              }}
            />
          </Field>

          {validationMessage && (
            <div className={styles.validationMessage}>{validationMessage}</div>
          )}

          <div className={styles.buttonContainer}>
            <SubmitButtonMail
              text="Submit"
              btnType="primary"
              onClick={handleContinue}
              disabled={!formValid || !isInfoConfirmed}
            />
          </div>
        </FormCard>
      </FieldGroup>
    ) : null;
  };

  return getCurrentStep();
};
